import { useState, useRef } from 'react'
import {
  Search,
  Loader2,
  X,
  Sparkles,
  List as ListIcon,
  Quote as QuoteIcon,
  Info,
} from 'lucide-react'
import { meetingsAPI } from '../../services/api'
import { decodeHtmlEntities } from '../../utils/displayText'

const EXAMPLES = [
  'Summarize this meeting',
  'What did we commit to?',
  'What ideas or suggestions came up?',
]

/**
 * A bullet may arrive as "Speaker: said something". Split so the speaker reads
 * as a soft label without changing the text the model produced.
 */
function splitSpeaker(line) {
  const m = /^([^:]{1,40}):\s+(.+)$/.exec(line)
  if (m) return { speaker: m[1], text: m[2] }
  return { speaker: null, text: line }
}

function SourceList({ sources }) {
  if (!sources || sources.length === 0) return null
  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        From the transcript
      </p>
      <div className="space-y-2">
        {sources.map((s, i) => (
          <div
            key={`${s.segmentStartId ?? 'src'}-${i}`}
            className="rounded-lg border border-gray-100 bg-gray-50/70 px-3 py-2"
          >
            {s.speaker && (
              <span className="mr-1 text-xs font-semibold text-gray-600">{s.speaker}:</span>
            )}
            <span className="text-xs text-gray-500">{decodeHtmlEntities(s.text)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Render the answer body adaptively by format. */
function AnswerBody({ result }) {
  const { format, answer, bullets, quotes, notFound } = result

  // Honest "not in this meeting" state — kept small, never a big empty block.
  if (notFound || format === 'none') {
    return (
      <div className="flex items-start gap-2 text-gray-600">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
        <p className="text-sm leading-relaxed">{decodeHtmlEntities(answer)}</p>
      </div>
    )
  }

  // Yes/no or single fact — one compact line, no padding into a block.
  if (format === 'short') {
    return <p className="text-[15px] font-medium leading-relaxed text-gray-900">{decodeHtmlEntities(answer)}</p>
  }

  // Bulleted list — lists of commitments, ideas, decisions, who-said-what.
  if (format === 'list') {
    return (
      <div>
        {answer && <p className="mb-2 text-sm text-gray-700">{decodeHtmlEntities(answer)}</p>}
        <ul className="space-y-1.5">
          {bullets.map((b, i) => {
            const { speaker, text } = splitSpeaker(b)
            return (
              <li key={i} className="flex gap-2 text-sm text-gray-800">
                <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-400" />
                <span className="leading-relaxed">
                  {speaker && <span className="font-semibold text-gray-900">{speaker}: </span>}
                  {decodeHtmlEntities(text)}
                </span>
              </li>
            )
          })}
        </ul>
      </div>
    )
  }

  // Verbatim quote(s) with attribution.
  if (format === 'quote') {
    return (
      <div>
        {answer && <p className="mb-2 text-sm text-gray-700">{decodeHtmlEntities(answer)}</p>}
        <div className="space-y-2">
          {quotes.map((q, i) => (
            <blockquote
              key={i}
              className="border-l-2 border-primary-300 bg-primary-50/40 py-1.5 pl-3 pr-2"
            >
              <p className="text-sm italic leading-relaxed text-gray-800">“{decodeHtmlEntities(q.text)}”</p>
              {q.speaker && <p className="mt-1 text-xs font-medium text-gray-500">— {q.speaker}</p>}
            </blockquote>
          ))}
        </div>
      </div>
    )
  }

  // prose (default) — summaries, explanations, assessments.
  return (
    <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-900">{decodeHtmlEntities(answer)}</p>
  )
}

const FORMAT_META = {
  short: { Icon: Sparkles, label: 'Answer' },
  prose: { Icon: Sparkles, label: 'Answer' },
  list: { Icon: ListIcon, label: 'Found in this meeting' },
  quote: { Icon: QuoteIcon, label: 'What was said' },
  none: { Icon: Info, label: 'Nothing on that' },
}

export default function MeetingSearchBar({ meetingId }) {
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [asked, setAsked] = useState('')
  const inputRef = useRef(null)

  const run = async (q) => {
    const trimmed = (q ?? '').trim()
    if (!trimmed || loading) return
    setLoading(true)
    setError(null)
    setAsked(trimmed)
    try {
      const res = await meetingsAPI.askMeeting(meetingId, trimmed)
      setResult(res?.data ?? null)
      if (!res?.data) setError('Something went wrong. Please try again.')
    } catch (err) {
      if (err?.status === 404) {
        setError('Meeting search is not turned on yet.')
      } else {
        setError('Something went wrong. Please try again.')
      }
      setResult(null)
    } finally {
      setLoading(false)
    }
  }

  const dismiss = () => {
    setResult(null)
    setError(null)
    setAsked('')
    setQuery('')
    inputRef.current?.focus()
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      run(query)
    }
  }

  const meta = result ? FORMAT_META[result.format] ?? FORMAT_META.prose : null
  const showPanel = loading || error || result

  return (
    <div className="my-12 md:my-16 w-full md:w-1/2 mx-auto">
      {/* Search bar */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search this meeting — what was said, decided, or committed to…"
          className="block w-full rounded-2xl border border-gray-200 bg-white py-3 pl-11 pr-28 text-[15px] leading-6 text-gray-900 shadow-sm focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-100"
        />
        <button
          type="button"
          onClick={() => run(query)}
          disabled={loading || !query.trim()}
          className="absolute right-2 top-1/2 inline-flex -translate-y-1/2 items-center gap-1.5 rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-40"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Ask
        </button>
      </div>

      {/* Example prompts — only before the first search */}
      {!showPanel && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => {
                setQuery(ex)
                run(ex)
              }}
              className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-200"
            >
              {ex}
            </button>
          ))}
        </div>
      )}

      {/* Result panel — inline, dismissible, directly below the bar */}
      {showPanel && (
        <div className="glass mt-3 rounded-2xl p-5">
          <div className="mb-2 flex items-center gap-2">
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
                <span className="text-xs font-semibold uppercase tracking-wide text-primary-600">
                  Searching this meeting
                </span>
              </>
            ) : (
              <>
                {meta && <meta.Icon className="h-4 w-4 text-primary-600" />}
                <span className="text-xs font-semibold uppercase tracking-wide text-primary-600">
                  {error ? 'Search' : meta?.label}
                </span>
              </>
            )}
            <button
              type="button"
              onClick={dismiss}
              className="ml-auto rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {asked && !loading && (
            <p className="mb-3 text-xs text-gray-400">“{asked}”</p>
          )}

          {loading ? (
            <div className="animate-pulse space-y-2">
              <div className="h-3 w-full rounded bg-gray-100" />
              <div className="h-3 w-5/6 rounded bg-gray-100" />
              <div className="h-3 w-2/3 rounded bg-gray-100" />
            </div>
          ) : error ? (
            <p className="text-sm text-amber-700">{error}</p>
          ) : result ? (
            <>
              <AnswerBody result={result} />
              <SourceList sources={result.sources} />
            </>
          ) : null}
        </div>
      )}
    </div>
  )
}
